<script setup lang="ts">
import { useQuoteListStore } from '~~/stores/quoteListStore'

/**
 * „Dodaj do listy zapytania" — akcja towarzysząca CTA wyceny.
 *
 * Sklep katalogowy nie ma koszyka, ale klient B2B prawie nigdy nie pyta o jedną
 * pozycję. Ten przycisk zbiera produkty w przeglądarce; wysyła je dopiero
 * strona listy, jednym zapytaniem.
 *
 * Renderuje się WYŁĄCZNIE w trybie katalogowym — w lavmagu i youlooku rolę
 * zbierania produktów pełni koszyk.
 */
const props = withDefaults(defineProps<{
  /**
   * Liczba albo cyfrowy ciąg — `Product.id` z API jest typu string, a karta
   * produktu podaje go bez konwersji.
   */
  idProduct: number | string
  /**
   * Wybrany wariant (kombinacja) — bez niego rozmiar ginął po drodze i był
   * widoczny tylko w SKU. Brak/0 = produkt bez wariantu.
   */
  idProductAttribute?: number | string | null
  /** Opis wariantu do pokazania na liście, np. „Rozmiar: XXL, Kolor: Blekitny". */
  attributes?: string
  name?: string
  /** SKU wybranego wariantu, gdy wołający go zna; inaczej SKU produktu. */
  reference?: string
  image?: string | null
  link?: string | null
  /**
   * Nakład startowy. Klient doprecyzuje go na stronie listy. Brak = minimalny
   * nakład marki (BrandConfig.quoteMinQuantity, proles: 50) — start od 1 szt.
   * kłócił się z „od 50 sztuk" na stronie „O nas".
   */
  quantity?: number
}>(), {
  idProductAttribute: 0,
  attributes: '',
  name: '',
  reference: '',
  image: null,
  link: null,
  quantity: undefined,
})

const quoteList = useQuoteListStore()
const feedback = ref<string | null>(null)
const isError = ref(false)

onMounted(() => quoteList.hydrate())

// Store trzyma identyfikatory jako liczby, więc porównania muszą dostać liczbę —
// inaczej „już na liście" nigdy nie byłoby prawdą dla id przyszłego z API.
const productId = computed(() => Number(props.idProduct))
const productAttributeId = computed(() => {
  const value = Number(props.idProductAttribute ?? 0)

  return Number.isInteger(value) && value > 0 ? value : 0
})

const alreadyOnList = computed(() => quoteList.contains(productId.value, productAttributeId.value))
const quantityOnList = computed(() => quoteList.quantityOf(productId.value, productAttributeId.value))

function add() {
  const added = quoteList.add({
    id_product: props.idProduct,
    id_product_attribute: props.idProductAttribute,
    attributes: props.attributes,
    quantity: props.quantity ?? quoteList.minQuantity(),
    name: props.name,
    reference: props.reference,
    image: props.image,
    link: props.link,
  })

  isError.value = !added
  feedback.value = added
    ? 'Dodano do listy zapytania.'
    : quoteList.lastError

  // Komunikat znika sam — to potwierdzenie, nie ostrzeżenie wymagające decyzji.
  if (added) {
    setTimeout(() => { feedback.value = null }, 4000)
  }
}
</script>

<template>
  <div class="quote-list-button">
    <button
      type="button"
      class="quote-list-button__action"
      :class="{ 'quote-list-button__action--on-list': alreadyOnList }"
      @click="add"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        aria-hidden="true"
      >
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" />
        <path d="M9 13h6M12 10v6" />
      </svg>
      <span>{{ alreadyOnList ? 'Dodaj kolejne sztuki' : 'Dodaj do listy zapytania' }}</span>
    </button>

    <p
      v-if="alreadyOnList"
      class="quote-list-button__status"
    >
      Na liście: {{ quantityOnList }} szt. —
      <NuxtLink to="/lista-zapytania">
        przejdź do listy
      </NuxtLink>
    </p>

    <p
      v-if="feedback"
      class="quote-list-button__feedback"
      :class="{ 'quote-list-button__feedback--error': isError }"
      role="status"
      aria-live="polite"
    >
      {{ feedback }}
    </p>
  </div>
</template>

<style scoped>
.quote-list-button {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.quote-list-button__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.875rem 1.25rem;
  border: 1px solid var(--color-border, #d4d4d4);
  border-radius: var(--radius-md, 8px);
  background: transparent;
  color: var(--color-text, #151515);
  font-weight: 600;
  cursor: pointer;
}

.quote-list-button__action:hover {
  border-color: var(--color-text, #151515);
}

.quote-list-button__action--on-list {
  border-style: dashed;
}

.quote-list-button__status,
.quote-list-button__feedback {
  margin: 0;
  font-size: 0.875rem;
  color: var(--color-text-muted, #6b6b6b);
}

.quote-list-button__feedback--error {
  color: var(--color-danger, #b3261e);
}
</style>
